import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../api/axios';
import ProductCard from './ProductCard';
import AddProductModal from './AddProductModal';

const RejectedSubmissions = () => {
    const { user } = useContext(AuthContext);
    const [rejectedProducts, setRejectedProducts] = useState([]);
    const [pendingProducts, setPendingProducts] = useState([]); // <-- NEW STATE
    const [editingProduct, setEditingProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const headers = { 'Authorization': `Bearer ${token}` };

                // Fetch both Rejected AND Pending at the same time
                const [rejectedRes, pendingRes] = await Promise.all([
                    api.get('/products/my-submissions?status=REJECTED', { headers }),
                    api.get('/products/my-submissions?status=PENDING', { headers })
                ]);

                setRejectedProducts(rejectedRes.data);
                setPendingProducts(pendingRes.data);
            } catch (error) {
                console.error("Failed to fetch submissions", error);
            }
        };

        // Only fetch if the user is NOT an Admin (i.e., Employee/Curator)
        if (user?.role && user.role !== 'ADMIN') {
            fetchSubmissions();
        }
    }, [user]);

    const handleEditRejected = (product) => {
        setEditingProduct(product);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingProduct(null);
    };

    const handleProductResubmitted = () => {
        handleModalClose();
        // Remove from rejected and instantly move it to pending so the UI feels fast!
        setRejectedProducts(prev => prev.filter(p => p.id !== editingProduct?.id));
        setPendingProducts(prev => [...prev, editingProduct]);
    };

    // If they have no pending and no rejected products, don't show the component at all
    if (rejectedProducts.length === 0 && pendingProducts.length === 0) return null;

    return (
        <div style={{ marginBottom: '30px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* --- REJECTED SECTION (Red/Alert) --- */}
            {rejectedProducts.length > 0 && (
                <div style={{ padding: '20px', backgroundColor: '#fff5f5', border: '2px dashed #f56565', borderRadius: '8px' }}>
                    <h3 style={{ color: '#c53030', marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        ⚠️ Action Required: Rejected Submissions ({rejectedProducts.length})
                    </h3>
                    <p style={{ color: '#742a2a', marginBottom: '20px' }}>
                        The following products were reviewed by an Admin and require updates before they can be listed.
                    </p>

                    <div className="products-grid">
                        {rejectedProducts.map(product => (
                            <div key={product.id} style={{ border: '1px solid #fc8181', borderRadius: '8px', overflow: 'hidden', backgroundColor: 'white' }}>
                                <ProductCard product={product} userRole={user?.role} />
                                <div style={{ padding: '10px', backgroundColor: '#fed7d7', textAlign: 'center' }}>
                                    <button
                                        onClick={() => handleEditRejected(product)}
                                        className="btn-submit"
                                        style={{ backgroundColor: '#c53030', width: '100%' }}
                                    >
                                        Read Feedback & Fix
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* --- PENDING SECTION (Orange/Warning) --- */}
            {pendingProducts.length > 0 && (
                <div style={{ padding: '20px', backgroundColor: '#fffaf0', border: '2px dashed #ed8936', borderRadius: '8px' }}>
                    <h3 style={{ color: '#c05621', marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        ⏳ Pending Approval ({pendingProducts.length})
                    </h3>
                    <p style={{ color: '#7b341e', marginBottom: '20px' }}>
                        You have successfully submitted these products. They are currently waiting for Admin review.
                    </p>

                    <div className="products-grid">
                        {pendingProducts.map(product => (
                            <div key={product.id} style={{ border: '1px solid #fbd38d', borderRadius: '8px', overflow: 'hidden', backgroundColor: 'white', opacity: 0.85 }}>
                                {/* Setting a slight opacity makes it look "inactive/pending" */}
                                <ProductCard product={product} userRole={user?.role} />
                                <div style={{ padding: '10px', backgroundColor: '#feebc8', textAlign: 'center', color: '#c05621', fontWeight: 'bold' }}>
                                    In Queue
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Render the AddProductModal for fixing rejected items */}
            {isModalOpen && (
                <AddProductModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    onProductAdded={handleProductResubmitted}
                    productToEdit={editingProduct}
                />
            )}
        </div>
    );
};

export default RejectedSubmissions;